"use client";

import { LeadThrough, TestDriveRequestStatus } from "@alba-cars/common-modules";
import parsePhoneNumberFromString from "libphonenumber-js";
import { z } from "zod";

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const testDriveSchema = z.object({
  testerName: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(255, "Name must be less than 255 characters")
    .transform((str) => str.trim())
    .refine((val) => val.length > 0, "Name is required"),

  testerMobile: z
    .string()
    .min(1, "Mobile number is required")
    .refine((value) => {
      try {
        const phoneNumber = parsePhoneNumberFromString(value);
        if (!phoneNumber) return false;

        return phoneNumber.isValid() && phoneNumber.isPossible();
      } catch {
        return false;
      }
    }, "Please enter a valid mobile number"),

  testerEmail: z
    .string()
    .email("Invalid email format")
    .regex(emailRegex, "Invalid email format")
    .optional()
    .or(z.literal("")),

  vehicle: z.object({
    existingId: z.string().min(1, "Vehicle ID is required"),
  }),

  requestedDate: z.string().refine((date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return selectedDate >= today && selectedDate <= thirtyDaysFromNow && !isNaN(selectedDate.getTime());
  }, "Please select a valid date within the next 30 days"),

  selectedTime: z
    .string()
    .refine(
      (time) => /^(0?[1-9]|1[0-2]):[0-5][0-9] (am|pm)$/i.test(time),
      "Please enter a valid time in the format HH:MM am/pm",
    ),

  status: z.nativeEnum(TestDriveRequestStatus),
  leadThrough: z.nativeEnum(LeadThrough),
});

export type TestDriveFormData = z.infer<typeof testDriveSchema>;
